/**
 * 지원 언어와 언어 결정 규칙. 순수 함수만 둔다 (브라우저 API 는 호출부가 넘긴다).
 */

export const LOCALES = ['ko', 'en', 'ja', 'zh-Hans', 'zh-Hant', 'es', 'pt-BR'] as const
export type Locale = (typeof LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'en'

/**
 * 번역이 다 들어가서 메뉴에 보여도 되는 언어.
 * 번역은 진행 순서상 마지막 단계라, 끝난 언어만 여기에 추가한다.
 * 이 목록에 없는 언어는 브라우저 언어로 감지돼도 고르지 않는다.
 */
export const ENABLED_LOCALES: readonly Locale[] = LOCALES

/** 메뉴에 보여줄 이름. 각 언어 자신의 표기로 쓴다. */
export const LOCALE_NATIVE_NAME: Record<Locale, string> = {
  ko: '한국어',
  en: 'English',
  ja: '日本語',
  'zh-Hans': '简体中文',
  'zh-Hant': '繁體中文',
  es: 'Español',
  'pt-BR': 'Português (BR)',
}

/** AI 프롬프트에 "이 언어로 써라" 라고 적을 때 쓰는 영어 이름. */
export const LOCALE_ENGLISH_NAME: Record<Locale, string> = {
  ko: 'Korean',
  en: 'English',
  ja: 'Japanese',
  'zh-Hans': 'Simplified Chinese as used in mainland China',
  'zh-Hant': 'Traditional Chinese as used in Taiwan and Hong Kong',
  es: 'Spanish (neutral Latin American and European readers)',
  'pt-BR': 'Brazilian Portuguese',
}

/** Intl API 와 <html lang> 에 넣는 태그. 한자 글리프를 언어별로 맞게 고르려면 lang 이 정확해야 한다. */
export const LOCALE_TAG: Record<Locale, string> = {
  ko: 'ko-KR',
  en: 'en',
  ja: 'ja-JP',
  'zh-Hans': 'zh-Hans',
  'zh-Hant': 'zh-Hant',
  es: 'es',
  'pt-BR': 'pt-BR',
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
}

/**
 * 브라우저 언어 태그 하나를 우리 언어로 옮긴다. 못 옮기면 null.
 * 중국어는 지역으로 간체/번체를 가른다.
 */
export function mapBrowserTag(tag: string): Locale | null {
  const lower = tag.toLowerCase()
  const [lang, ...rest] = lower.split('-')

  if (lang === 'zh') {
    // 문자 체계가 명시돼 있으면 그걸 따른다 (zh-Hant-HK, zh-Hans-SG 등).
    if (rest.includes('hant')) return 'zh-Hant'
    if (rest.includes('hans')) return 'zh-Hans'
    if (rest.some((r) => ['tw', 'hk', 'mo'].includes(r))) return 'zh-Hant'
    // zh-CN, zh-SG, 지역 없는 zh 는 간체.
    return 'zh-Hans'
  }
  if (lang === 'pt') return 'pt-BR'
  if (lang === 'ko' || lang === 'en' || lang === 'ja' || lang === 'es') return lang
  return null
}

/**
 * 언어 결정 순서: 사용자가 고른 언어 → 브라우저 언어 → en.
 * 켜져 있지 않은 언어는 건너뛴다.
 */
export function resolveLocale(input: {
  stored: string | null
  browser: readonly string[]
  enabled?: readonly Locale[]
}): Locale {
  const enabled = input.enabled ?? ENABLED_LOCALES
  if (isLocale(input.stored) && enabled.includes(input.stored)) return input.stored
  for (const tag of input.browser) {
    const mapped = mapBrowserTag(tag)
    if (mapped && enabled.includes(mapped)) return mapped
  }
  return DEFAULT_LOCALE
}

/**
 * 토스 결제창 언어. KO·EN·JA·ZH 만 지원한다.
 * zh-Hant 는 ZH 가 간체 결제창이라 EN 으로 연다 (대만·홍콩 사용자에게 간체를 강요하지 않는다).
 * 지원하지 않는 언어는 EN. 따로 안내 문구는 넣지 않는다.
 */
export function tossLanguage(locale: Locale): 'KO' | 'EN' | 'JA' | 'ZH' {
  switch (locale) {
    case 'ko':
      return 'KO'
    case 'ja':
      return 'JA'
    case 'zh-Hans':
      return 'ZH'
    default:
      return 'EN'
  }
}

/** 한중일 글꼴을 따로 받아야 하는 언어 */
export function needsCjkFont(locale: Locale): locale is 'ja' | 'zh-Hans' | 'zh-Hant' {
  return locale === 'ja' || locale === 'zh-Hans' || locale === 'zh-Hant'
}
